<script setup lang="ts">
import {
  VISIBILITY_COMPOSITION_OPTIONS,
  VISIBILITY_TRIGGER_DEFINITIONS,
  type UtcCalendarPeriod,
  type VisibilityComposition,
  type VisibilityPolicy,
  type VisibilityTrigger,
} from "#bb/lib/visibilityPolicy";
import {
  CONDITIONAL_VISIBILITY_PERSISTENCE_FAILURE_BEHAVIORS,
  type ConditionalVisibilityPersistenceFailureBehavior,
} from "#bb/lib/conditionalVisibilityPersistence";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import RuleBlockPicker from "../rules/RuleBlockPicker.vue";
import { flattenBlocks } from "../rules/ruleModel";

const props = defineProps<{ id: string }>();
const { field, block, patch } = useInlineBlockEditor(props.id);
const { getBlock } = useBlockRegistry();

const policy = field<VisibilityPolicy>("policy", {
  composition: "all",
  triggers: [],
});
const newTriggerType = shallowRef<VisibilityTrigger["type"]>("lifetime-maximum");
const timerBlockId = field<string>("timerBlockId", "");
const hiddenMessageEnabled = field<boolean>("hiddenMessageEnabled", false);
const hiddenMessage = field<string>(
  "hiddenMessage",
  "This content is no longer available.",
);
const resetEnabled = field<boolean>("resetEnabled", false);
const resetLimit = field<number>("resetLimit", 1);
const resetLabel = field<string>("resetLabel", "Request another attempt");
const resetExhaustedMessage = field<string>(
  "resetExhaustedMessage",
  "No resets remain for this content.",
);
const persistenceFailureBehavior =
  field<ConditionalVisibilityPersistenceFailureBehavior>(
    "persistenceFailureBehavior",
    "show-content",
  );

const nestedTimers = computed(() =>
  flattenBlocks(block.value?.children)
    .filter((candidate) => candidate.componentId === ComponentDefinitionIds.TIMER)
    .map((candidate) => ({
      ...candidate,
      type: getBlock(candidate.componentId ?? "")?.type ?? "Timer",
      icon: getBlock(candidate.componentId ?? "")?.icon,
    })),
);
const selectedTimerIsValid = computed(
  () => !timerBlockId.value
    || nestedTimers.value.some((timer) => timer.id === timerBlockId.value),
);

const composition = computed({
  get: () => policy.value?.composition ?? "all",
  set: (value: VisibilityComposition) => {
    policy.value = { ...policy.value, composition: value };
  },
});

function createTrigger(type: VisibilityTrigger["type"]): VisibilityTrigger {
  const id = globalThis.crypto?.randomUUID?.() ?? `visibility-${Date.now()}-${Math.random()}`;
  if (type === "rolling-window") {
    return { id, type, maximum: 1, durationSeconds: 86400 };
  }
  if (type === "cooldown") {
    return { id, type, cooldownSeconds: 86400 };
  }
  if (type === "utc-calendar-window") {
    return { id, type, maximum: 1, period: "day", weekStartsOn: 1 };
  }
  return { id, type, maximum: 1 };
}

function addTrigger() {
  policy.value = {
    ...policy.value,
    triggers: [...(policy.value?.triggers ?? []), createTrigger(newTriggerType.value)],
  };
}

function removeTrigger(index: number) {
  policy.value = {
    ...policy.value,
    triggers: (policy.value?.triggers ?? []).filter((_, triggerIndex) => triggerIndex !== index),
  };
}

function updateTrigger(index: number, patch: Partial<VisibilityTrigger>) {
  policy.value = {
    ...policy.value,
    triggers: (policy.value?.triggers ?? []).map((trigger, triggerIndex) =>
      triggerIndex === index ? { ...trigger, ...patch } as VisibilityTrigger : trigger
    ),
  };
}

function numberValue(value: string | number, fallback = 1) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(1, Math.floor(parsed)) : fallback;
}

function setCooldownCap(index: number, enabled: boolean) {
  const trigger = policy.value.triggers[index];
  if (!trigger || trigger.type !== "cooldown") return;
  updateTrigger(index, {
    lifetimeMaximum: enabled ? trigger.lifetimeMaximum ?? 1 : undefined,
  });
}

function nonNegativeNumber(value: string | number, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : fallback;
}

function updateHiddenMessageEnabled(enabled: boolean) {
  if (enabled && block.value?.hiddenMessage === undefined) {
    patch({
      hiddenMessageEnabled: true,
      hiddenMessage: hiddenMessage.value,
    });
    return;
  }
  hiddenMessageEnabled.value = enabled;
}
</script>

<template>
  <EditorSection title="Eligibility policy" icon="lucide:list-checks" accent="container" default-open>
    <p class="text-[11.5px] text-muted-foreground">
      Each successful Show consumes one view. Use conditional rules, such as a timer completing, to hide the content.
    </p>

    <EditorField label="Composition">
      <Select :model-value="composition" @update:model-value="(value) => (composition = value as VisibilityComposition)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in VISIBILITY_COMPOSITION_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <div class="space-y-2">
      <div
        v-for="(condition, index) in policy.triggers"
        :key="condition.id ?? `${condition.type}-${index}`"
        class="space-y-3 rounded-md border bg-card p-3"
      >
        <div class="flex items-start justify-between gap-2">
          <div>
            <p class="text-[13px] font-medium">
              {{ VISIBILITY_TRIGGER_DEFINITIONS.find((item) => item.type === condition.type)?.label }}
            </p>
            <p class="text-[11.5px] text-muted-foreground">
              {{ VISIBILITY_TRIGGER_DEFINITIONS.find((item) => item.type === condition.type)?.description }}
            </p>
          </div>
          <Button variant="ghost" size="icon" class="size-7 shrink-0 text-destructive" @click="removeTrigger(index)">
            <Icon name="lucide:trash-2" class="size-3.5" />
          </Button>
        </div>

        <EditorField
          v-if="condition.type === 'lifetime-maximum' || condition.type === 'rolling-window' || condition.type === 'utc-calendar-window'"
          label="Maximum views"
        >
          <Input
            :model-value="condition.maximum"
            type="number"
            min="1"
            class="h-8 rounded-sm bg-muted text-[13px]"
            @update:model-value="(value) => updateTrigger(index, { maximum: numberValue(value as string) })"
          />
        </EditorField>

        <EditorField v-if="condition.type === 'rolling-window'" label="Window duration">
          <div class="flex items-center gap-2">
            <Input
              :model-value="condition.durationSeconds"
              type="number"
              min="1"
              class="h-8 rounded-sm bg-muted text-[13px]"
              @update:model-value="(value) => updateTrigger(index, { durationSeconds: numberValue(value as string) })"
            />
            <span class="text-[12.5px] text-muted-foreground">seconds</span>
          </div>
        </EditorField>

        <template v-if="condition.type === 'cooldown'">
          <EditorField label="Cooldown">
            <div class="flex items-center gap-2">
              <Input
                :model-value="condition.cooldownSeconds"
                type="number"
                min="1"
                class="h-8 rounded-sm bg-muted text-[13px]"
                @update:model-value="(value) => updateTrigger(index, { cooldownSeconds: numberValue(value as string) })"
              />
              <span class="text-[12.5px] text-muted-foreground">seconds</span>
            </div>
          </EditorField>
          <EditorField label="Lifetime cap">
            <div class="flex items-center justify-between">
              <span class="text-[12.5px] text-muted-foreground">Also stop after a total number of views.</span>
              <Switch
                :model-value="condition.lifetimeMaximum !== undefined"
                @update:model-value="(value) => setCooldownCap(index, value)"
              />
            </div>
          </EditorField>
          <EditorField v-if="condition.lifetimeMaximum !== undefined" label="Lifetime maximum">
            <Input
              :model-value="condition.lifetimeMaximum"
              type="number"
              min="1"
              class="h-8 rounded-sm bg-muted text-[13px]"
              @update:model-value="(value) => updateTrigger(index, { lifetimeMaximum: numberValue(value as string) })"
            />
          </EditorField>
        </template>

        <template v-if="condition.type === 'utc-calendar-window'">
          <EditorField label="UTC period">
            <Select
              :model-value="condition.period"
              @update:model-value="(value) => updateTrigger(index, { period: value as UtcCalendarPeriod })"
            >
              <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hour">Hour</SelectItem>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="year">Year</SelectItem>
              </SelectContent>
            </Select>
          </EditorField>
          <EditorField v-if="condition.period === 'week'" label="Week starts on">
            <Select
              :model-value="String(condition.weekStartsOn ?? 1)"
              @update:model-value="(value) => updateTrigger(index, { weekStartsOn: Number(value) })"
            >
              <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Monday</SelectItem>
                <SelectItem value="0">Sunday</SelectItem>
              </SelectContent>
            </Select>
          </EditorField>
        </template>
      </div>
    </div>

    <EditorField label="Add condition">
      <div class="flex gap-2">
        <Select v-model="newTriggerType">
          <SelectTrigger class="h-8 flex-1 rounded-sm bg-muted text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="definition in VISIBILITY_TRIGGER_DEFINITIONS"
              :key="definition.type"
              :value="definition.type"
            >
              {{ definition.label }}
            </SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" class="h-8 rounded-sm" @click="addTrigger">
          <Icon name="lucide:plus" class="mr-1 size-3.5" />
          Add
        </Button>
      </div>
    </EditorField>
  </EditorSection>

  <EditorSection
    title="Timer lifecycle"
    icon="lucide:timer-reset"
    accent="container"
    :default-open="false"
  >
    <p class="text-[11.5px] text-muted-foreground">
      Select one nested timer to keep the same view active across refreshes until that timer completes.
    </p>

    <EditorField
      label="Nested timer"
      hint="Only Timer blocks inside this container can control its active view."
      :error="selectedTimerIsValid ? undefined : 'The selected timer is no longer nested in this container.'"
    >
      <RuleBlockPicker
        v-model="timerBlockId"
        :blocks="nestedTimers"
        placeholder="Select a nested timer"
        empty-label="Add a Timer block inside this container first."
      />
    </EditorField>
  </EditorSection>

  <EditorSection
    title="Availability safeguards"
    icon="lucide:shield-alert"
    accent="container"
    :default-open="false"
  >
    <p class="text-[11.5px] text-muted-foreground">
      Choose whether content remains available when per-user view history cannot be loaded or saved.
    </p>

    <EditorField
      label="State service failure"
      hint="Show content is backward compatible. Hide content is stricter but can make content unavailable during API or authentication failures."
    >
      <RadioGroup v-model="persistenceFailureBehavior" class="gap-2">
        <Label
          v-for="option in CONDITIONAL_VISIBILITY_PERSISTENCE_FAILURE_BEHAVIORS"
          :key="option.value"
          :for="`${props.id}-persistence-${option.value}`"
          :class="[
            'flex cursor-pointer items-start gap-2.5 rounded-sm border px-3 py-2.5 transition-colors',
            persistenceFailureBehavior === option.value
              ? 'border-primary/50 bg-primary/5'
              : 'border-border bg-card hover:bg-muted/50',
          ]"
        >
          <RadioGroupItem
            :id="`${props.id}-persistence-${option.value}`"
            :value="option.value"
            class="mt-0.5"
          />
          <span class="min-w-0 space-y-0.5">
            <span class="block text-[12.5px] font-medium text-foreground">
              {{ option.label }}
            </span>
            <span class="block text-[11px] font-normal leading-4 text-muted-foreground">
              {{ option.description }}
            </span>
          </span>
        </Label>
      </RadioGroup>
    </EditorField>
  </EditorSection>

  <EditorSection
    title="Hidden state"
    icon="lucide:eye-off"
    accent="container"
    :default-open="false"
  >
    <EditorField label="Custom message">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">
          Show a templated Markdown message when the content is hidden.
        </span>
        <Switch
          :model-value="hiddenMessageEnabled"
          @update:model-value="updateHiddenMessageEnabled"
        />
      </div>
    </EditorField>

    <EditorField
      v-if="hiddenMessageEnabled"
      label="Message"
      hint="Supports Markdown and template values from report and answer data."
    >
      <EditorMarkdownDrawerField
        v-model="hiddenMessage"
        title="Edit hidden message"
        description="Update the Markdown shown when this content is unavailable."
        button-label="Edit hidden message…"
        empty-preview="No hidden message configured."
      />
    </EditorField>

    <EditorField label="Allow reset">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">
          Clear view history and restart the selected timer.
        </span>
        <Switch v-model="resetEnabled" />
      </div>
    </EditorField>

    <template v-if="resetEnabled">
      <div
        v-if="timerBlockId && !selectedTimerIsValid"
        class="flex items-start gap-2 rounded-sm border border-warning/40 bg-warning/10 px-3 py-2 text-[12px] text-warning"
      >
        <Icon name="lucide:triangle-alert" class="mt-0.5 size-3.5 shrink-0" />
        <span>Select a valid nested timer or clear the timer selection.</span>
      </div>

      <EditorField
        label="Lifetime reset limit"
        hint="The limit is stored per user for this conditional visibility container."
      >
        <Input
          :model-value="resetLimit"
          type="number"
          min="0"
          step="1"
          class="h-8 rounded-sm bg-muted text-[13px]"
          @update:model-value="(value) => (resetLimit = nonNegativeNumber(value as string, 1))"
        />
      </EditorField>

      <EditorField label="Reset link label">
        <Input
          v-model="resetLabel"
          class="h-8 rounded-sm bg-muted text-[13px]"
          placeholder="Request another attempt"
        />
      </EditorField>

      <EditorField
        label="No resets message"
        hint="Shown after the candidate has used every available reset. Supports Markdown and template values."
      >
        <EditorMarkdownDrawerField
          v-model="resetExhaustedMessage"
          title="Edit no-resets message"
          description="Update the Markdown shown after every available reset has been used."
          button-label="Edit no-resets message…"
          empty-preview="No separate no-resets message configured. The hidden message will be used."
        />
      </EditorField>
    </template>
  </EditorSection>
</template>
